import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CCard, CCardGroup } from "@coreui/react";
import { toast } from "react-toastify";
import { useTranslationContext } from "@/context/TranslationContext";
import DummyUserForm from "../dummyUserForm";
import { dummyUser } from "@/constants/translation/dummyUser.translation";
import { breadcrumb_key, messages } from "@/constants/translation/common.translation";
import DummyUserValidationClass from "@/helpers/validations/dummyUser";
import { dummyUser as dummyUserRoutes } from "@/libs/routes";
import { useAddDummyUser } from "@/hooks/useDummyUser";

/**
 * Component for adding a new Dummy User.
 * @returns {JSX.Element} The rendered AddDummyUser component.
 */
const AddDummyUser = () => {
  const t = useTranslationContext();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { mutate: doAddDummyUser } = useAddDummyUser(
    (response) => {
      toast.success(response?.message || t(messages.toast.dummyUser.added));
      navigate(dummyUserRoutes.list);
    },
    (error) => {
      toast.error(error?.response?.data?.message || t(messages.toast.dummyUser.addFailed));
      setIsSubmitting(false);
    }
  );

  const handleAddDummyUser = (values) => {
    setIsSubmitting(true);
    doAddDummyUser({
      first_name: values.first_name,
      last_name: values.last_name,
      email: values.email,
      house_number: values.house_number,
      street: values.street,
      city: values.city,
      country: values.country,
      zip_code: values.zip_code,
      latitude: values.latitude,
      longitude: values.longitude,
    });
  };

  const initialValues = useMemo(
    () => ({
      first_name: "",
      last_name: "",
      email: "",
      house_number: "",
      street: "",
      city: "",
      country: "",
      zip_code: "",
      latitude: "",
      longitude: "",
    }),
    []
  );

  const validationSchema = useMemo(() => DummyUserValidationClass.dummyUserValidation(t), [t]);

  return (
    <CCardGroup>
      <CCard className="p-4">
        <h1 className="text-center">{t(dummyUser.add.main_heading)}</h1>
        <DummyUserForm
          initialValues={initialValues}
          onSubmit={handleAddDummyUser}
          isSubmitting={isSubmitting}
          validationSchema={validationSchema}
        />
      </CCard>
    </CCardGroup>
  );
};

AddDummyUser.breadCrumbArray = [
  {
    name: breadcrumb_key.dummyUser?.list,
    pathname: dummyUserRoutes.list,
    active: false,
  },
  {
    name: breadcrumb_key.dummyUser?.add,
    pathname: dummyUserRoutes.add,
    active: true,
  },
];

export default AddDummyUser;
